import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle2, Download, FileText, Loader2, RefreshCw, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * /download/:magnetId — user-facing landing that the newsletter email
 * links to instead of the raw PDF endpoint.
 *
 * Why this exists: browsers download files silently to the Downloads
 * folder. Users click the email link, the browser fires the download
 * without any visible page transition, and then people click again
 * thinking nothing happened — resulting in duplicate downloads. This
 * page ships a clear "your download started" screen, auto-triggers the
 * actual file fetch exactly once, and provides an explicit
 * "download again" button for the "I checked and I don't have it"
 * case.
 *
 * The download itself still comes from the existing
 * `/api/lead-magnets/:id/download` FastAPI endpoint — this component is
 * purely UX packaging around it.
 */

const MAGNET_TITLES = {
  'koopplanner-spanje': {
    nl: 'De Complete Koopplanner Spanje',
    en: 'The Complete Spain Buying Planner',
  },
};

const MAGNET_FILENAMES = {
  'koopplanner-spanje': 'koopplanner-spanje.pdf',
};

const DownloadGids = () => {
  const { magnetId } = useParams();
  const title = MAGNET_TITLES[magnetId]?.nl || 'Jouw CasaOthers gids';
  // Public-friendly URL. We intentionally avoid the older
  // `/api/lead-magnets/...` path because it exposes the internal
  // "lead-magnets" marketing term to anyone reading the email or the
  // browser address bar. The `/api/gidsen/<filename>.pdf` alias reads
  // as a normal file download and hides the funnel-tooling naming.
  const filename = MAGNET_FILENAMES[magnetId] || `${magnetId}.pdf`;
  const downloadUrl = `${API_URL}/api/gidsen/${filename}`;
  const [downloadState, setDownloadState] = useState('starting'); // starting | done | retrying
  const [error, setError] = useState('');

  // Idempotency guard. React 18 StrictMode simulates unmount → remount
  // in dev mode which resets any useRef; a sessionStorage flag keyed by
  // magnetId survives that cycle so the auto-download fires exactly once
  // per session. We include a timestamp so a return-visit later still
  // triggers a fresh download.
  const sessionKey = `dl-fired-${magnetId}`;
  const alreadyFiredForThisSession = () => {
    try {
      const raw = window.sessionStorage.getItem(sessionKey);
      if (!raw) return false;
      // Consider the flag stale after 60s so users who come back later
      // in the same tab still get an auto-download.
      return Date.now() - Number(raw) < 60_000;
    } catch (_) {
      return false;
    }
  };
  const markFired = () => {
    try { window.sessionStorage.setItem(sessionKey, String(Date.now())); }
    catch (_) { /* private-mode storage disabled — best-effort only */ }
  };

  const triggerDownload = () => {
    setError('');
    try {
      // Synthetic anchor click. Chromium/Firefox/Safari all handle
      // `Content-Disposition: attachment` on <a download> as a single
      // network request — no iframe navigation + separate download-engine
      // request combo that the hidden-iframe approach would cause
      // (which is what led to the original "two files downloaded" bug).
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.setAttribute('download', '');
      // Kept out of the DOM tab-order and cleaned up immediately after.
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // The browser has now claimed the download. Flip UI to "done" so
      // the user sees an unambiguous confirmation.
      setDownloadState('done');
    } catch (e) {
      setError(e.message || 'Er ging iets mis met de download.');
      setDownloadState('retrying');
    }
  };

  useEffect(() => {
    if (alreadyFiredForThisSession()) {
      // Second mount from StrictMode (or a fast refresh) — skip the
      // auto-fire but still show the "done" state so the UI isn't stuck
      // spinning.
      setDownloadState('done');
      return;
    }
    markFired();
    triggerDownload();
  }, []);

  const retry = () => {
    setDownloadState('starting');
    window.setTimeout(triggerDownload, 100);
  };

  return (
    <div style={styles.wrap} data-testid="download-page">
      <Helmet>
        <title>Download je gids — CasaOthers</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div style={styles.card}>
        <div style={styles.iconWrap}>
          {downloadState === 'starting' && <Loader2 size={48} className="spin" color="#C5A059" />}
          {downloadState === 'done' && <CheckCircle2 size={48} color="#166534" />}
          {downloadState === 'retrying' && <FileText size={48} color="#C5A059" />}
        </div>

        {downloadState === 'starting' && (
          <>
            <h1 style={styles.h1}>Je download start automatisch…</h1>
            <p style={styles.p}>
              We sturen <strong>&quot;{title}&quot;</strong> nu naar je Downloads-map.
              Dit duurt slechts een paar seconden.
            </p>
          </>
        )}

        {downloadState === 'done' && (
          <>
            <h1 style={styles.h1}>Download gestart ✓</h1>
            <p style={styles.p}>
              <strong>&quot;{title}&quot;</strong> staat nu klaar in je Downloads-map.
              Check je browser-menu of de map <em>Downloads</em> op je computer of telefoon.
            </p>
            <p style={styles.hint}>
              Kun je het bestand niet vinden? Klik hieronder om opnieuw te starten —
              we versturen géén tweede e-mail.
            </p>
            <button
              type="button"
              style={styles.btnSecondary}
              onClick={retry}
              data-testid="download-retry"
            >
              <RefreshCw size={16} /> Download opnieuw
            </button>
          </>
        )}

        {downloadState === 'retrying' && (
          <>
            <h1 style={styles.h1}>Klik om je gids te downloaden</h1>
            <p style={styles.p}>
              De automatische download werkte niet in jouw browser. Klik op
              de knop om het bestand alsnog op te halen.
            </p>
            {error && <p style={styles.error}>{error}</p>}
            <a
              href={downloadUrl}
              style={styles.btn}
              data-testid="download-manual"
              onClick={() => setDownloadState('done')}
            >
              <Download size={16} /> Download {title}
            </a>
          </>
        )}

        <div style={styles.divider} />

        <div style={styles.nextSteps}>
          <h2 style={styles.h2}>Meer weten over kopen in Spanje?</h2>
          <Link to="/gidsen" style={styles.linkRow} data-testid="download-goto-gidsen">
            <span>Bekijk al onze gratis gidsen</span>
            <ArrowRight size={16} />
          </Link>
          <Link to="/woningen" style={styles.linkRow} data-testid="download-goto-woningen">
            <span>Actueel woningaanbod aan de Costa Blanca</span>
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

const styles = {
  wrap: { minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem 1.5rem', background: '#FAF9F6' },
  card: { maxWidth: '520px', width: '100%', background: 'white', border: '1px solid #EAE5DD', padding: '2.5rem 2rem', textAlign: 'center' },
  iconWrap: { marginBottom: '1rem' },
  h1: { fontFamily: "'Playfair Display', serif", fontSize: '1.75rem', color: '#1A1918', margin: '0 0 0.75rem' },
  h2: { fontFamily: "'Playfair Display', serif", fontSize: '1.05rem', color: '#1A1918', margin: '0 0 1rem', textAlign: 'left' },
  p: { color: '#585755', fontSize: '0.95rem', lineHeight: 1.6, margin: '0 0 1rem' },
  hint: { color: '#8C8B88', fontSize: '0.85rem', lineHeight: 1.5, margin: '0 0 1rem' },
  error: { color: '#991B1B', fontSize: '0.9rem', margin: '0 0 1rem' },
  btn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
    padding: '0.85rem 1.5rem', background: '#1A1918', color: '#C5A059',
    fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.08em', textTransform: 'uppercase',
    textDecoration: 'none', border: 'none', cursor: 'pointer', width: '100%',
    boxSizing: 'border-box', marginTop: '0.5rem',
  },
  btnSecondary: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
    padding: '0.75rem 1.25rem', background: '#FAF6EE', color: '#8C6E28',
    fontWeight: 600, fontSize: '0.8rem', letterSpacing: '0.03em',
    border: '1px solid #E4D9C1', cursor: 'pointer',
  },
  divider: { height: 1, background: '#EAE5DD', margin: '2rem 0 1.5rem' },
  nextSteps: { textAlign: 'left' },
  linkRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '0.75rem 1rem', background: '#FAF9F6', border: '1px solid #EAE5DD',
    color: '#1A1918', textDecoration: 'none', fontSize: '0.9rem',
    marginBottom: '0.5rem',
  },
};

export default DownloadGids;
